import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  hasActiveRows,
  isColumnPrompted,
  isMatrixRowActive,
  MatrixCompatibilityError,
  matrixQueries,
  type MatrixColumnType,
  type MatrixListItemUi,
} from "@/domains/matrix";
import { InfoIcon, PanelLeftCloseIcon, PanelLeftOpenIcon } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { MatrixSidebar } from "../../../components/matrix-sidebar";
import { MatrixToolbar } from "../../../components/matrix-toolbar";
import { MatrixGrid } from "../../../components/matrix-grid";
import { MatrixCellSheet } from "../../../components/matrix-cell-sheet";
import { DeleteMatrixDialog } from "../../../components/delete-matrix-dialog";
import { useUpdateMatrix } from "../../../hooks/use-update-matrix";
import { useMatrixRowActions } from "../../../hooks/use-matrix-row-actions";
import { useAddMatrixRow } from "../../../hooks/use-add-matrix-row";
import { useDeleteMatrixRows } from "../../../hooks/use-delete-matrix-rows";
import { useAddMatrixColumn } from "../../../hooks/use-add-matrix-column";
import { useUpdateMatrixColumn } from "../../../hooks/use-update-matrix-column";
import { useDeleteMatrixColumn } from "../../../hooks/use-delete-matrix-column";
import { useUpdateMatrixCell } from "../../../hooks/use-update-matrix-cell";
import { useRunMatrix } from "../../../hooks/use-run-matrix";
import { extractErrorMessage } from "../../../lib/extract-error-message";
import { pickFirstMatrixError } from "../../../lib/pick-first-matrix-error";
import { isOptimisticRow } from "../../../lib/optimistic-row";
import { useMatrixRailCollapsed } from "../../../lib/matrix-rail-state";
import {
  buildBadgeColorMap,
  type BadgeColorMap,
} from "../../../lib/value-badge";
import { parseListItems } from "../../../lib/list-items";
import type {
  CellAddress,
  ColumnPopoverTarget,
  SortState,
} from "../../../types";
import { MatrixLoadingCard } from "./matrix-loading-card";
import { MatrixErrorCard } from "./matrix-error-card";
import { MatrixCompatibilityView } from "./matrix-compatibility-view";
import { MatrixErrorAlert } from "../../../components/matrix-error-alert";
import { MatrixRowSelectionBar } from "./matrix-row-selection-bar";

type MatrixDetailViewProps = {
  collectionId: string;
  matrices: MatrixListItemUi[];
};

type MatrixRowSelection = {
  collectionId: string;
  mode: "all" | "selected";
  rowIds: Set<string>;
};

export function startMatrixIfAvailable({
  hasActiveRow,
  canStartAnalysis,
  isStartingAnalysis,
  analysisIds,
  mutate,
}: {
  hasActiveRow: boolean;
  canStartAnalysis: boolean;
  isStartingAnalysis: boolean;
  analysisIds: string[];
  mutate: (input: { analysisIds: string[] }) => void;
}) {
  if (hasActiveRow || !canStartAnalysis || isStartingAnalysis) return;
  mutate({ analysisIds });
}

export function MatrixDetailView({
  collectionId,
  matrices,
}: MatrixDetailViewProps) {
  const {
    data: matrix,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery(matrixQueries.detail(collectionId));

  const [compatibilityError, setCompatibilityError] =
    useState<MatrixCompatibilityError | null>(null);
  let currentCompatibilityError = compatibilityError;
  if (
    isError &&
    error instanceof MatrixCompatibilityError &&
    error !== compatibilityError
  ) {
    currentCompatibilityError = error;
    setCompatibilityError(error);
  } else if (
    compatibilityError &&
    (compatibilityError.collectionId !== collectionId ||
      (!isError && !isLoading && matrix))
  ) {
    currentCompatibilityError = null;
    setCompatibilityError(null);
  }

  const [rowSelection, setRowSelection] = useState<MatrixRowSelection>(() => ({
    collectionId,
    mode: "all",
    rowIds: new Set(),
  }));
  let currentRowSelection = rowSelection;
  if (rowSelection.collectionId !== collectionId) {
    currentRowSelection = {
      collectionId,
      mode: "all",
      rowIds: new Set(),
    };
    setRowSelection(currentRowSelection);
  }
  const [sortState, setSortState] = useState<SortState>(null);
  const [selectedCell, setSelectedCell] = useState<CellAddress | null>(null);
  const [editingCell, setEditingCell] = useState<CellAddress | null>(null);
  const [columnPopover, setColumnPopover] =
    useState<ColumnPopoverTarget | null>(null);
  const [expandedCell, setExpandedCell] = useState<CellAddress | null>(null);
  const [isDeleteRequested, setIsDeleteRequested] = useState(false);
  const [isRailCollapsed, setRailCollapsed] = useMatrixRailCollapsed();

  const renameMatrix = useUpdateMatrix();
  const addRow = useAddMatrixRow(collectionId);
  const deleteRows = useDeleteMatrixRows(collectionId);
  const addColumn = useAddMatrixColumn(collectionId);
  const updateColumn = useUpdateMatrixColumn(collectionId);
  const deleteColumn = useDeleteMatrixColumn(collectionId);
  const updateCell = useUpdateMatrixCell(collectionId);
  const runMatrix = useRunMatrix(collectionId);
  const rowActions = useMatrixRowActions(matrices);

  if (currentCompatibilityError) {
    return (
      <MatrixCompatibilityView
        collectionId={collectionId}
        matrices={matrices}
        analysisIds={currentCompatibilityError.incompatibleAnalysisIds}
        onRetry={() => void refetch()}
        isRetrying={isFetching}
        retryError={
          isError && !(error instanceof MatrixCompatibilityError)
            ? extractErrorMessage(
                error,
                "Could not reload this matrix. Try again.",
              )
            : null
        }
      />
    );
  }

  if (isLoading) return <MatrixLoadingCard />;

  if (isError || !matrix) {
    return (
      <MatrixErrorCard
        message={extractErrorMessage(
          error,
          "Something went wrong while loading this matrix.",
        )}
      />
    );
  }

  const rows = matrix.rows;
  let currentEditingCell = editingCell;
  if (
    currentEditingCell &&
    rows.some(
      (row) =>
        row.analysisId === currentEditingCell?.analysisId &&
        isMatrixRowActive(row),
    )
  ) {
    currentEditingCell = null;
    setEditingCell(null);
  }
  let selection = currentRowSelection;
  const currentRowIds = new Set(rows.map((row) => row.analysisId));
  const selectedRowIds = new Set(
    Array.from(selection.rowIds).filter((analysisId) =>
      currentRowIds.has(analysisId),
    ),
  );
  if (
    selection.mode === "selected" &&
    selectedRowIds.size !== selection.rowIds.size
  ) {
    selection = { ...selection, rowIds: selectedRowIds };
    setRowSelection(selection);
  }
  const hasPromptColumns = matrix.columns.some(isColumnPrompted);
  const hasActiveRow = hasActiveRows(matrix);
  const requestedIds =
    selection.mode === "selected"
      ? rows
          .filter((row) => selectedRowIds.has(row.analysisId))
          .map((row) => row.analysisId)
      : rows.map((row) => row.analysisId);
  const hasStaleEmptySelection =
    selection.mode === "selected" && selectedRowIds.size === 0;
  const hasIdleResearch = rows.some(
    (row) =>
      requestedIds.includes(row.analysisId) &&
      matrix.columns.some(
        (column) =>
          isColumnPrompted(column) && row.cells[column.key]?.status === "idle",
      ),
  );
  const canStartAnalysis =
    hasPromptColumns &&
    !hasActiveRow &&
    !hasStaleEmptySelection &&
    hasIdleResearch;
  const startDisabledReason = !hasPromptColumns
    ? "Add a prompt to at least one column before starting an analysis."
    : hasActiveRow
      ? "Wait for the current matrix run to finish before starting it again."
      : hasStaleEmptySelection
        ? "The selected rows are no longer available. Select a current row before starting."
        : !hasIdleResearch
          ? "There are no open research cells in the selected rows."
          : null;
  const hasActiveSelectedRow = rows.some(
    (row) => selectedRowIds.has(row.analysisId) && isMatrixRowActive(row),
  );

  const badgeColorsByColumn = new Map<string, BadgeColorMap>();
  for (const column of matrix.columns) {
    if (column.type !== "badge") continue;
    const values: string[] = [];
    for (const row of rows) {
      const cellValue = row.cells[column.key]?.value;
      if (cellValue) values.push(...parseListItems(cellValue));
    }
    badgeColorsByColumn.set(column.key, buildBadgeColorMap(values));
  }
  const isSubmittingColumn =
    addColumn.isPending || updateColumn.isPending || deleteColumn.isPending;
  const columnSubmitError = addColumn.error
    ? extractErrorMessage(addColumn.error, "Failed to add column.")
    : updateColumn.error
      ? extractErrorMessage(updateColumn.error, "Failed to update column.")
      : deleteColumn.error
        ? extractErrorMessage(deleteColumn.error, "Failed to remove column.")
        : null;

  const activeError = pickFirstMatrixError([
    {
      title: "Could not save the matrix name",
      error: renameMatrix.error,
      fallback: "Failed to save the matrix.",
    },
    {
      title: "Could not add the row",
      error: addRow.error,
      fallback: "Failed to add the row.",
    },
    {
      title: "Could not delete the selected rows",
      error: deleteRows.error,
      fallback: "Failed to delete the selected rows.",
    },
    {
      title: "Could not save the column",
      error: addColumn.error,
      fallback: "Failed to add the column.",
    },
    {
      title: "Could not save the column",
      error: updateColumn.error,
      fallback: "Failed to save the column.",
    },
    {
      title: "Could not duplicate the matrix",
      error: rowActions.duplicateError,
      fallback: "Failed to duplicate the matrix.",
    },
    {
      title: "Could not remove the column",
      error: deleteColumn.error,
      fallback: "Failed to remove the column.",
    },
    {
      title: "Could not save the cell",
      error: updateCell.error,
      fallback: "Failed to save the cell.",
    },
    {
      title: "Could not start the analysis",
      error: runMatrix.error,
      fallback: "Failed to start the analysis.",
    },
  ]);

  function handleToggleRow(analysisId: string) {
    setRowSelection(() => {
      const next = new Set(selectedRowIds);
      if (next.has(analysisId)) next.delete(analysisId);
      else next.add(analysisId);
      return {
        collectionId,
        mode: next.size > 0 ? "selected" : "all",
        rowIds: next,
      };
    });
  }

  function handleToggleAllRows() {
    setRowSelection({
      collectionId,
      mode:
        rows.length > 0 && selectedRowIds.size !== rows.length
          ? "selected"
          : "all",
      rowIds:
        rows.length > 0 && selectedRowIds.size !== rows.length
          ? new Set(rows.map((row) => row.analysisId))
          : new Set(),
    });
  }

  function handleSort(columnKey: string) {
    setSortState((prev) => {
      if (prev?.columnKey !== columnKey) return { columnKey, direction: "asc" };
      if (prev.direction === "asc") return { columnKey, direction: "desc" };
      return null;
    });
  }

  function handleSelectCell(address: CellAddress | null) {
    setSelectedCell(address);
    if (!address) setEditingCell(null);
  }

  function handleCommitEdit(
    address: CellAddress,
    value: string,
    nextAddress?: CellAddress | null,
  ) {
    // A row that is still being created has no analysis id to write against.
    const row = rows.find(
      (candidate) => candidate.analysisId === address.analysisId,
    );
    if (
      isOptimisticRow(address.analysisId) ||
      (row && isMatrixRowActive(row))
    ) {
      setEditingCell(null);
      return;
    }
    updateCell.mutate({
      analysisId: address.analysisId,
      columnKey: address.columnKey,
      value,
    });

    if (nextAddress) {
      setSelectedCell(nextAddress);
      const nextColumn = matrix?.columns.find(
        (column) => column.key === nextAddress.columnKey,
      );
      const nextRow = rows.find(
        (candidate) => candidate.analysisId === nextAddress.analysisId,
      );
      setEditingCell(
        nextColumn &&
          nextRow &&
          !isColumnPrompted(nextColumn) &&
          !isMatrixRowActive(nextRow)
          ? nextAddress
          : null,
      );
      return;
    }
    setEditingCell(null);
  }

  function handleSubmitColumn(
    columnKey: string | null,
    values: {
      label: string;
      type: MatrixColumnType;
      displayDescription: string;
    },
  ) {
    if (hasActiveRow) return;
    if (columnKey) {
      updateColumn.mutate(
        { columnKey, input: values },
        { onSuccess: () => setColumnPopover(null) },
      );
    } else {
      addColumn.mutate(values, { onSuccess: () => setColumnPopover(null) });
    }
  }

  function handleRemoveColumn(columnKey: string) {
    if (hasActiveRow) return;
    deleteColumn.mutate(columnKey, {
      onSuccess: () => setColumnPopover(null),
    });
  }

  function handleResizeColumn(columnKey: string, width: number) {
    if (hasActiveRow) return;
    updateColumn.mutate({ columnKey, input: { width } });
  }

  function handleAddRow() {
    addRow.mutate(undefined, {
      onSuccess: (data) => {
        const firstEditable = matrix?.columns.find(
          (column) => !isColumnPrompted(column),
        );
        if (!firstEditable) return;
        const address = {
          analysisId: data.analysis_id,
          columnKey: firstEditable.key,
        };
        setSelectedCell(address);
        setEditingCell(address);
      },
    });
  }

  function handleDeleteSelectedRows() {
    const analysisIds = Array.from(selectedRowIds);
    if (
      analysisIds.length === 0 ||
      rows.some(
        (row) => analysisIds.includes(row.analysisId) && isMatrixRowActive(row),
      )
    ) {
      return;
    }
    deleteRows.mutate(analysisIds, {
      onSuccess: () =>
        setRowSelection({ collectionId, mode: "all", rowIds: new Set() }),
    });
  }

  function handleStartAnalysis() {
    startMatrixIfAvailable({
      hasActiveRow,
      canStartAnalysis,
      isStartingAnalysis: runMatrix.isPending,
      analysisIds: requestedIds,
      mutate: runMatrix.mutate,
    });
  }

  return (
    <>
      <title>Matrix - Researchly</title>
      {/* contain-size keeps this subtree out of the shell's height calculation,
          so it takes the space that is left instead of stretching the page. The
          -mb-4 lets the rail run to the footer; the content column puts the
          distance back. */}
      <div className="-mb-4 flex w-full max-w-full flex-1 flex-col gap-0 overflow-hidden contain-size lg:flex-row lg:gap-0">
        <MatrixSidebar matrices={matrices} activeCollectionId={collectionId} />

        <div className="mb-4 flex min-h-0 min-w-0 flex-1 flex-col gap-4 py-4 sm:py-6 lg:pr-6 lg:pl-6">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setRailCollapsed(!isRailCollapsed)}
              aria-label={
                isRailCollapsed
                  ? "Expand matrices sidebar"
                  : "Collapse matrices sidebar"
              }
              title={
                isRailCollapsed
                  ? "Expand matrices sidebar"
                  : "Collapse matrices sidebar"
              }
              className="text-muted-foreground hover:text-primary absolute top-1/2 left-0.5 hidden size-6 -translate-x-1/2 -translate-y-1/2 lg:inline-flex"
            >
              {isRailCollapsed ? (
                <PanelLeftOpenIcon className="size-3.5" />
              ) : (
                <PanelLeftCloseIcon className="size-3.5" />
              )}
            </Button>
            <div className="min-w-0">
              <MatrixToolbar
                name={matrix.name}
                question={matrix.question}
                collectionId={collectionId}
                matrices={matrices}
                onCommitName={(name) =>
                  renameMatrix.mutate({ collectionId, name })
                }
                onStartAnalysis={handleStartAnalysis}
                isStartingAnalysis={runMatrix.isPending}
                canStartAnalysis={canStartAnalysis}
                startDisabledReason={startDisabledReason}
                isSaving={renameMatrix.isPending}
                onRequestDelete={() => setIsDeleteRequested(true)}
                onDuplicate={() =>
                  rowActions.duplicate({ collectionId, name: matrix.name })
                }
                isDuplicating={rowActions.isDuplicating(collectionId)}
              />
            </div>
          </div>

          <div className="mt-4 flex min-h-8 items-center">
            {selectedRowIds.size > 0 ? (
              <div className="w-full">
                <MatrixRowSelectionBar
                  selectedCount={selectedRowIds.size}
                  isDeleting={deleteRows.isPending}
                  deleteDisabled={hasActiveSelectedRow}
                  onConfirmDelete={handleDeleteSelectedRows}
                />
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">
                Every row is one analysis, every column one of its fields. More
                in the info button{" "}
                <InfoIcon
                  className="inline size-3.5 align-[-2px]"
                  aria-hidden
                />{" "}
                above.
              </p>
            )}
          </div>

          {activeError && (
            <MatrixErrorAlert
              title={activeError.title}
              error={activeError.error}
              fallback={activeError.fallback}
            />
          )}

          <MatrixGrid
            columns={matrix.columns}
            rows={matrix.rows}
            badgeColorsByColumn={badgeColorsByColumn}
            selectedRowIds={selectedRowIds}
            sortState={sortState}
            selectedCell={selectedCell}
            editingCell={currentEditingCell}
            columnPopover={columnPopover}
            isSubmittingColumn={isSubmittingColumn}
            columnSubmitError={columnSubmitError}
            onOpenColumnPopover={setColumnPopover}
            onToggleRow={handleToggleRow}
            onToggleAllRows={handleToggleAllRows}
            onSort={handleSort}
            onSelectCell={handleSelectCell}
            onStartEdit={(address) => {
              const row = rows.find(
                (candidate) => candidate.analysisId === address.analysisId,
              );
              if (
                isOptimisticRow(address.analysisId) ||
                (row && isMatrixRowActive(row))
              ) {
                return;
              }
              setEditingCell(address);
            }}
            onCommitEdit={handleCommitEdit}
            onCancelEdit={() => setEditingCell(null)}
            onExpandCell={setExpandedCell}
            onResizeColumn={handleResizeColumn}
            hasActiveRows={hasActiveRow}
            onAddRow={handleAddRow}
            isAddingRow={addRow.isPending}
            onSubmitColumn={handleSubmitColumn}
            onRemoveColumn={handleRemoveColumn}
          />
        </div>
      </div>

      <DeleteMatrixDialog
        target={isDeleteRequested ? { collectionId, name: matrix.name } : null}
        matrices={matrices}
        activeCollectionId={collectionId}
        onClose={() => setIsDeleteRequested(false)}
      />

      <MatrixCellSheet
        row={
          matrix.rows.find(
            (row) => row.analysisId === expandedCell?.analysisId,
          ) ?? null
        }
        column={
          matrix.columns.find(
            (column) => column.key === expandedCell?.columnKey,
          ) ?? null
        }
        badgeColors={
          expandedCell
            ? badgeColorsByColumn.get(expandedCell.columnKey)
            : undefined
        }
        onClose={() => setExpandedCell(null)}
        onSave={(value) => {
          if (!expandedCell) return;
          const row = matrix.rows.find(
            (candidate) => candidate.analysisId === expandedCell.analysisId,
          );
          if (row && isMatrixRowActive(row)) return;
          updateCell.mutate(
            {
              analysisId: expandedCell.analysisId,
              columnKey: expandedCell.columnKey,
              value,
            },
            { onSuccess: () => setExpandedCell(null) },
          );
        }}
        isSaving={updateCell.isPending}
        saveError={
          updateCell.error
            ? extractErrorMessage(updateCell.error, "Failed to save the cell.")
            : null
        }
      />
    </>
  );
}
